import { useEffect, useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { notification } from 'antd';
import apiService from '../axios/apiService';
import ApiService from '../services/ApiService.js';
import { useUserSelectedBranchLocation } from './useUserSelectedBranchLocation.js';
import { useUserToken } from './useToken.js';
import { LOGIN_STEPS, USER_STATUS } from '../appContants.js';
import useLoginSteps from './login/useLoginSteps.js';
import usePasswordAuth from './login/usePasswordAuth.js';
import useOtpVerification from './login/useOtpVerification.js';
import useResetPassword from './login/useResetPassword.js';

const useLoginFlow = () => {
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isRegisterMsg, setIsRegisterMsg] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRef = useRef();
  const { unSetBranchLocation } = useUserSelectedBranchLocation();
  const { setUserToken } = useUserToken();

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    clearErrors,
  } = useForm();

  // Stable notification wrapper that sub-hooks can use without re-renders
  const notify = {
    success: (opts) => api.success({ showProgress: false, ...opts }),
    error: (opts) => api.error({ showProgress: false, ...opts }),
  };

  // --- Step navigation ---
  const steps = useLoginSteps();

  // --- Password auth ---
  const passwordAuth = usePasswordAuth({
    email: steps.email,
    onChallengeReceived: () => {
      steps.goToStep(LOGIN_STEPS.OTP);
      otp.bumpTimer();
    },
    onDeviceTrusted: async (user) => {
      setUserToken(user.signInUserSession.idToken.jwtToken);
      passwordAuth.setCognitoUser(user);
      await otp.setupSession();
    },
    notify,
  });

  // --- OTP verification ---
  const otp = useOtpVerification({
    cognitoUser: passwordAuth.cognitoUser,
    setCognitoUser: passwordAuth.setCognitoUser,
    formCredentials: passwordAuth.formCredentials,
    goToStep: steps.goToStep,
    notify,
  });

  // --- Reset password ---
  const resetPw = useResetPassword({
    email: steps.email,
    goToStep: steps.goToStep,
    resetAuth: passwordAuth.resetAuth,
    notify,
  });

  // --- Async wrapper to centralize loading + error handling ---
  const withLoading = useCallback(
    (fn) =>
      async (...args) => {
        try {
          setIsLoading(true);
          await fn(...args);
        } catch (error) {
          const msg =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message;
          if (msg) {
            notify.error({ message: 'Error', description: msg });
          }
        } finally {
          setIsLoading(false);
        }
      },
    [steps.email]
  );

  // --- Composed handlers exposed to the component ---

  const handleUserStatusSubmit = withLoading(async ({ email: emailValue }) => {
    const normalizedEmail = emailValue?.trim().toLowerCase();
    const response = await apiService.getUserStatus(normalizedEmail);
    const status = response?.status;

    steps.goToStep(steps.currentStep, {
      email: normalizedEmail,
      userStatus: status,
    });
    setValue('email', normalizedEmail);

    if (status === USER_STATUS.CONFIRMED) {
      steps.goToStep(LOGIN_STEPS.PASSWORD, {
        email: normalizedEmail,
        userStatus: status,
      });
      return;
    }

    if (
      status === USER_STATUS.UNCONFIRMED ||
      status === USER_STATUS.RESET_REQUIRED ||
      status === USER_STATUS.FORCE_CHANGE_PASSWORD
    ) {
      await ApiService.forgetPassword(normalizedEmail);
      notify.success({
        message: 'Verification Code Sent',
        description:
          'Enter the code sent to your email and choose a new password.',
      });
      steps.goToStep(LOGIN_STEPS.RESET_PASSWORD, {
        email: normalizedEmail,
        userStatus: status,
      });
      return;
    }

    if (status === USER_STATUS.DISABLED) {
      notify.error({
        message: 'Account Disabled',
        description: 'Your account is disabled. Please contact support.',
      });
      return;
    }

    notify.error({
      message: 'Error',
      description:
        'Unable to determine your account status. Please try again.',
    });
  });

  const handlePasswordLogin = withLoading(async ({ password }) => {
    try {
      await passwordAuth.handlePasswordLogin({ password }, reset);
    } catch (error) {
      const isUserNotConfirmed =
        error?.code === 'UserNotConfirmedException' ||
        error?.__type === 'UserNotConfirmedException' ||
        (typeof error?.message === 'string' &&
          error.message.toLowerCase().includes('not confirmed'));

      if (isUserNotConfirmed) {
        await passwordAuth.handleUnconfirmedUser();
        return;
      }
      throw error;
    }
  });

  const handleNewPasswordSubmit = withLoading(async (data) => {
    await passwordAuth.handleNewPasswordSubmit(data, reset);
  });

  const handleResetPasswordSubmit = withLoading(async (data) => {
    await resetPw.handleResetPasswordSubmit(data, reset);
  });

  const handleResendResetCode = withLoading(async () => {
    await resetPw.handleResendResetCode();
  });

  const handleOtpComplete = withLoading(async (value) => {
    await otp.handleOtpComplete(value, trustDevice);
  });

  const handleOtpResend = async () => {
    try {
      setIsLoading(true);
      return await otp.handleOtpResend();
    } catch {
      notify.error({ message: 'Error', description: 'Failed to resend OTP!' });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetLoginFlow = () => {
    steps.resetSteps();
    passwordAuth.resetAuth();
    otp.resetOtp();
    setTrustDevice(false);
    reset();
    clearErrors();
  };

  const renderTitle = () =>
    steps.getTitle(passwordAuth.cognitoUser?.challengeName);

  // --- Side effects ---
  useEffect(() => {
    const flag = localStorage.getItem('isRegister');
    if (flag === 'true') {
      setIsRegisterMsg(true);
      localStorage.removeItem('isRegister');
    }
  }, []);

  useEffect(() => {
    unSetBranchLocation();
  }, []);

  return {
    register,
    errors,
    handleSubmit,
    inputRef,
    contextHolder,
    isLoading,
    cognitoUser: passwordAuth.cognitoUser,
    showPassword: passwordAuth.showPassword,
    formDataForOtp: passwordAuth.formCredentials,
    timerKey: otp.timerKey,
    isOtpTimerActive: otp.isOtpTimerActive,
    isRegisterMsg,
    currentStep: steps.currentStep,
    email: steps.email,
    togglePasswordVisibility: passwordAuth.togglePasswordVisibility,
    resetLoginFlow,
    renderTitle,
    handleUserStatusSubmit,
    handlePasswordLogin,
    handleNewPasswordSubmit,
    handleResetPasswordSubmit,
    handleResendResetCode,
    handleOtpComplete,
    handleOtpResend,
    setIsOtpTimerActive: otp.setIsOtpTimerActive,
    trustDevice,
    setTrustDevice,
  };
};

export default useLoginFlow;
